import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Webhook, Copy, RotateCw, Eye, EyeOff, Code2, ExternalLink, Check, Download, Globe, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

/** Copie robuste avec triple fallback :
 *  1) navigator.clipboard (HTTPS + contexte sécurisé)
 *  2) document.execCommand('copy') sur un textarea temporaire (ancien mais fiable)
 *  3) Sélection manuelle (l'user fait Cmd/Ctrl+C)
 */
async function robustCopy(text) {
  // 1. API moderne
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return { ok: true, method: 'clipboard' };
    }
  } catch (_) { /* fall through */ }
  // 2. Fallback execCommand
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    ta.style.left = '-9999px';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) return { ok: true, method: 'execCommand' };
  } catch (_) { /* fall through */ }
  return { ok: false };
}

const Field = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const handleCopy = async () => {
    const res = await robustCopy(value);
    if (res.ok) {
      setCopied(true);
      toast.success(`${label} copié`);
      setTimeout(() => setCopied(false), 1500);
    } else {
      // Fallback ultime : sélectionne le champ pour que l'user fasse Cmd/Ctrl+C
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
      toast.info('Sélection faite — utilise Cmd/Ctrl+C pour copier');
    }
  };

  return (
    <div className="space-y-1">
      <p className="text-xs text-zinc-500 uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          className="flex-1 text-xs bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-zinc-200 font-mono select-all focus:outline-none focus:border-purple-500"
          data-testid={testid}
        />
        <Button
          size="icon"
          variant="outline"
          className={`h-8 w-8 border-zinc-700 transition-colors ${copied ? 'border-emerald-500 text-emerald-400' : ''}`}
          onClick={handleCopy}
          title="Copier (ou clique sur le champ puis Cmd/Ctrl+C)"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
};

export const WebhookCard = () => {
  const [config, setConfig] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [showSample, setShowSample] = useState(false);
  const [showDomainEdit, setShowDomainEdit] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [savingDomain, setSavingDomain] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/inbound/config`, { withCredentials: true });
      setConfig(res.data);
      setDomainInput(res.data.custom_domain || '');
    } catch (err) {
      console.error('Webhook config load error:', err);
      if (err.response?.status === 403) {
        setConfig({ _forbidden: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveDomain = async () => {
    setSavingDomain(true);
    try {
      const cleaned = (domainInput || '').trim();
      await axios.put(`${API}/inbound/custom-domain`, { custom_domain: cleaned }, { withCredentials: true });
      await load();
      toast.success(cleaned ? 'Domaine personnalisé enregistré' : 'Domaine personnalisé retiré');
      setShowDomainEdit(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingDomain(false);
    }
  };

  const copy = async (text, label) => {
    const res = await robustCopy(text);
    if (res.ok) {
      toast.success(`${label} copié`);
    } else {
      toast.info('Sélectionne manuellement puis Cmd/Ctrl+C (copie bloquée dans cet iframe)');
    }
  };

  const rotate = async () => {
    if (!window.confirm("Régénérer la clé ? L'ancienne sera invalidée immédiatement et tu devras la mettre à jour dans Make.com.")) return;
    setRotating(true);
    try {
      await axios.post(`${API}/inbound/rotate-key`, {}, { withCredentials: true });
      await load();
      toast.success('Nouvelle clé générée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la rotation');
    } finally {
      setRotating(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="py-8 text-center text-sm text-zinc-500">Chargement...</CardContent>
      </Card>
    );
  }

  if (config?._forbidden) return null;

  if (!config) return null;

  return (
    <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2" data-testid="webhook-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Webhook className="w-5 h-5 text-purple-400" />
          Webhook alertes immobilières
        </CardTitle>
        <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
          Reçois automatiquement les nouvelles annonces depuis <strong>Make.com</strong>, <strong>Zapier</strong> ou <strong>n8n</strong>.
          Configure tes alertes mail sur LeBonCoin/SeLoger/IAD/PAP/Logic-Immo, redirige vers une boîte Gmail dédiée,
          et fais pousser les annonces parsées vers cette URL — chaque annonce devient une fiche bien automatiquement.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Domaine personnalisé pour endpoint stable cross-deploy */}
        <div className={`p-3 rounded-lg border ${config.is_using_custom ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-amber-500/5 border-amber-500/30'}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-start gap-2">
              <Globe className={`w-4 h-4 mt-0.5 ${config.is_using_custom ? 'text-emerald-400' : 'text-amber-400'}`} />
              <div>
                <p className={`text-xs font-semibold ${config.is_using_custom ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {config.is_using_custom ? 'Domaine personnalisé actif' : 'URL de preview (peut changer en production)'}
                </p>
                <p className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed">
                  {config.is_using_custom
                    ? 'Cette URL restera stable même après déploiement.'
                    : 'Configure un domaine personnalisé pour que l\'URL Webhook reste fixe une fois déployée — sinon Make.com cassera à chaque changement d\'URL preview.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDomainEdit((v) => !v)}
              className="text-[11px] text-zinc-400 hover:text-white underline whitespace-nowrap"
              data-testid="toggle-domain-edit"
            >
              {showDomainEdit ? 'Annuler' : 'Configurer'}
            </button>
          </div>
          {showDomainEdit && (
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder="https://app.tondomaine.fr (laisse vide pour preview)"
                className="flex-1 text-xs bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-zinc-200 font-mono focus:outline-none focus:border-emerald-500"
                data-testid="domain-input"
              />
              <Button
                size="sm"
                onClick={saveDomain}
                disabled={savingDomain}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-domain"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {savingDomain ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          )}
          {config.is_using_custom && !showDomainEdit && (
            <p className="text-[11px] text-emerald-400 font-mono mt-1 break-all">{config.custom_domain}</p>
          )}
        </div>

        <Field
          label="Endpoint URL (POST)"
          value={config.endpoint_url}
          testid="webhook-endpoint"
        />
        <Field
          label="Header d'authentification"
          value={`${config.header_name}: ${showKey ? config.api_key : config.api_key.replace(/.(?=.{4})/g, '•')}`}
          testid="webhook-header"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-zinc-700 text-zinc-300"
            onClick={() => setShowKey((v) => !v)}
            data-testid="toggle-show-key"
          >
            {showKey ? <><EyeOff className="w-3.5 h-3.5 mr-1.5" /> Masquer la clé</> : <><Eye className="w-3.5 h-3.5 mr-1.5" /> Afficher la clé</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-zinc-700 text-zinc-300"
            onClick={() => copy(config.api_key, 'Clé')}
            data-testid="copy-key"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier la clé seule
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            onClick={rotate}
            disabled={rotating}
            data-testid="rotate-key"
          >
            <RotateCw className={`w-3.5 h-3.5 mr-1.5 ${rotating ? 'animate-spin' : ''}`} /> Régénérer la clé
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-zinc-700 text-zinc-300"
            onClick={() => setShowSample((v) => !v)}
            data-testid="toggle-sample"
          >
            <Code2 className="w-3.5 h-3.5 mr-1.5" /> {showSample ? 'Cacher' : 'Voir'} l'exemple JSON
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
            onClick={() => {
              const content = [
                "# Configuration Webhook — Home MDB 2A",
                "",
                `URL (POST) : ${config.endpoint_url}`,
                `Header d'authentification : ${config.header_name}: ${config.api_key}`,
                "",
                "# Auth alternative (si Make.com n'accepte pas les headers custom)",
                `Authorization: Bearer ${config.api_key}`,
                "",
                "# Endpoint de debug (pour tester sans créer de fiche)",
                `${config.debug_url || config.endpoint_url.replace('/property-alert', '/debug')}`,
                "",
                "# Exemple de body JSON",
                JSON.stringify(config.sample_payload, null, 2),
              ].join("\n");
              const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = "webhook-mdb-config.txt";
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              toast.success('Fichier téléchargé');
            }}
            data-testid="download-config"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Télécharger en .txt
          </Button>
        </div>

        {showSample && (
          <div className="space-y-2">
            <p className="text-xs text-zinc-500">Exemple de payload à envoyer depuis Make.com :</p>
            <pre className="text-[11px] bg-zinc-950 border border-zinc-800 rounded p-3 text-emerald-300 font-mono overflow-x-auto" data-testid="sample-payload">
{JSON.stringify(config.sample_payload, null, 2)}
            </pre>
            <Button
              size="sm"
              variant="outline"
              className="border-zinc-700 text-zinc-300"
              onClick={() => copy(JSON.stringify(config.sample_payload, null, 2), 'JSON exemple')}
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier l'exemple
            </Button>
          </div>
        )}

        <div className="text-xs text-zinc-400 leading-relaxed bg-purple-500/5 border border-purple-500/20 rounded-lg p-3 space-y-1">
          <p className="font-semibold text-purple-300 flex items-center gap-1">
            <ExternalLink className="w-3.5 h-3.5" /> Tutoriel Make.com en 5 minutes
          </p>
          <ol className="list-decimal list-inside space-y-1 ml-1">
            <li>Crée une boîte Gmail dédiée (ex : <code className="text-emerald-400">alertes.mdb@gmail.com</code>) et redirige toutes tes alertes LBC/SeLoger/PAP/IAD vers elle.</li>
            <li>Sur <a href="https://make.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400 underline">make.com</a> (gratuit 1 000 ops/mois), crée un scénario : <em>Gmail Watch Emails → Text Parser → HTTP Make a Request</em>.</li>
            <li>Dans le module HTTP : méthode <code className="text-emerald-400">POST</code>, URL ci-dessus, header <code className="text-emerald-400">{config.header_name}</code> avec ta clé, body JSON comme l'exemple.</li>
            <li>Active le scénario : toutes les nouvelles annonces apparaîtront automatiquement dans <strong>Recherche &gt; Auto-importées</strong> avec une notif.</li>
          </ol>
          <div className="mt-3 pt-2 border-t border-purple-500/20 space-y-1">
            <p className="font-semibold text-amber-300">🛠 Ça ne marche pas ? (HTTP 422 ou 401)</p>
            <ul className="list-disc list-inside space-y-0.5 text-zinc-400">
              <li><strong>Astuce n°1</strong> : appelle l'URL <code className="text-emerald-400 break-all">{config.debug_url}</code> au lieu de l'URL prod — elle te renvoie exactement ce qu'elle a reçu + te dit pourquoi ton payload est refusé.</li>
              <li><strong>Tolérance</strong> : les champs <code className="text-emerald-400">prix</code> / <code className="text-emerald-400">surface</code> / <code className="text-emerald-400">rooms</code> acceptent maintenant des strings ("38 000 €", "80 m²", "T4"). Seuls <code className="text-emerald-400">titre</code> et <code className="text-emerald-400">url</code> sont obligatoires.</li>
              <li><strong>Auth alternative</strong> : si Make.com n'arrive pas à envoyer un header custom <code className="text-emerald-400">X-Webhook-Key</code>, tu peux utiliser <code className="text-emerald-400">Authorization: Bearer &lt;clé&gt;</code> à la place — les 2 sont acceptés.</li>
              <li><strong>Content-Type</strong> : JSON ET form-urlencoded sont acceptés.</li>
            </ul>
          </div>
          <p className="text-zinc-500 mt-2">
            <strong>Idempotence :</strong> la même URL d'annonce envoyée 2× n'est créée qu'une seule fois (le compteur de "vues" est juste incrémenté).
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default WebhookCard;
